import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { ordersAPI, formatKSH } from "../services/api";
import DataTable from "../components/DataTable";
import OrderModal from "../components/OrderModal";
import SearchableSelect from "../components/SearchableSelect";

const DATE_RANGE_OPTIONS = [
  { value: "today",   label: "Today" },
  { value: "week",    label: "Last 7 Days" },
  { value: "month",   label: "Last 30 Days" },
  { value: "quarter", label: "Last 90 Days" },
];

const getLocalDateString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Parse YYYY-MM-DD as local midnight so date comparisons are timezone-safe
const parseDateLocal = (str) => {
  if (!str) return null;
  const s = String(str).slice(0, 10);
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const Orders = () => {
  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState("");
  const [showOrderModal, setShowOrderModal] = useState(false);

  /* ── data ────────────────────────────────────────────────────────── */
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ordersAPI.getAll();
      setOrders(response.data);
    } catch (err) {
      setError("Failed to load orders");
      console.error("Orders error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  /* ── client-side filtering ───────────────────────────────────────── */
  const filtered = useMemo(() => {
    let result = [...orders];

    if (statusFilter === "ordered")     result = result.filter(o => o.order_status === "Ordered");
    if (statusFilter === "not_ordered") result = result.filter(o => o.order_status === "Not Ordered");

    if (dateRange) {
      const todayMidnight = new Date();
      todayMidnight.setHours(0, 0, 0, 0);

      if (dateRange === "today") {
        const todayStr = getLocalDateString();
        result = result.filter(o => o.order_date && String(o.order_date).slice(0, 10) === todayStr);
      } else {
        const days = { week: 7, month: 30, quarter: 90 }[dateRange];
        if (days) {
          const cutoff = new Date(todayMidnight);
          cutoff.setDate(cutoff.getDate() - days);
          result = result.filter(o => {
            const d = parseDateLocal(o.order_date);
            return d && d >= cutoff;
          });
        }
      }
    }

    return result;
  }, [orders, statusFilter, dateRange]);

  /* ── stats ───────────────────────────────────────────────────────── */
  const completed    = orders.filter(o => o.order_status === "Ordered");
  const pending      = orders.filter(o => o.order_status === "Not Ordered");
  const totalRevenue = completed.reduce((s, o) => s + parseFloat(o.order_amount || 0), 0);

  /* ── helpers ─────────────────────────────────────────────────────── */
  const formatDate = (d) => {
    const parsed = parseDateLocal(d);
    return parsed ? parsed.toLocaleDateString("en-KE") : "-";
  };
  const formatDateTime = (d) => d ? new Date(d).toLocaleString("en-KE") : "-";

  /* ── columns ─────────────────────────────────────────────────────── */
  const columns = [
    {
      key: "_no", label: "#",
      sortable: false, export: false,
      thClass: "w-12",
      render: (_, __, idx) => (
        <span className="text-gray-400 font-mono text-xs">{idx + 1}</span>
      ),
    },
    {
      key: "customer_name", label: "Customer",
      exportValue: r => r.customer_name,
      render: (val, row) => (
        <Link
          to={`/customers/${row.customer_id}`}
          className="font-medium text-primary-600 hover:text-primary-800 hover:underline"
        >{val}</Link>
      ),
    },
    {
      key: "contact_number", label: "Phone",
      exportValue: r => r.contact_number || "",
      render: val => val || <span className="text-gray-400 text-xs">—</span>,
    },
    {
      key: "order_status", label: "Status",
      exportValue: r => r.order_status,
      render: val => (
        <span className={`badge text-xs ${val === "Ordered" ? "badge-success" : "badge-danger"}`}>
          {val === "Ordered" ? "Completed" : "Pending"}
        </span>
      ),
    },
    {
      key: "order_amount", label: "Amount (KSH)",
      exportValue: r => parseFloat(r.order_amount || 0).toFixed(2),
      render: val => (
        <span className="font-semibold text-success-700">
          {formatKSH(val || 0)}
        </span>
      ),
    },
    {
      key: "order_date", label: "Order Date",
      exportValue: r => formatDate(r.order_date),
      render: val => formatDate(val),
    },
    {
      key: "salesperson_name", label: "Salesperson",
      exportValue: r => r.salesperson_name || "",
      render: val => val || <span className="text-gray-400 text-xs">—</span>,
    },
    {
      key: "notes", label: "Notes",
      exportValue: r => r.notes || "",
      tdClass: "max-w-xs",
      render: val => val
        ? <span className="text-gray-700 text-xs line-clamp-2">{val}</span>
        : <span className="text-gray-400 text-xs">—</span>,
    },
    {
      key: "created_by_name", label: "Created By",
      exportValue: r => r.created_by_name || "",
      render: (val, row) => (
        <div className="text-xs">
          <div>{val}</div>
          <div className="text-gray-400">{formatDateTime(row.created_at)}</div>
        </div>
      ),
    },
    {
      key: "actions", label: "",
      sortable: false, export: false,
      render: (_, row) => (
        <Link
          to={`/customers/${row.customer_id}`}
          className="btn btn-secondary text-xs py-1 px-2.5 whitespace-nowrap"
        >
          View Customer
        </Link>
      ),
    },
  ];

  const searchFields = [
    "customer_name", "contact_number", "notes",
    "created_by_name", "salesperson_name", "order_status",
  ];

  /* ── render ──────────────────────────────────────────────────────── */
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">All Orders</h1>
          <p className="mt-1 text-sm text-gray-600">Track and manage customer orders</p>
        </div>
        <button
          onClick={() => setShowOrderModal(true)}
          className="btn btn-primary inline-flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Order
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Orders",  value: orders.length,    color: "text-gray-700",    bg: "bg-gray-50" },
          { label: "Completed",     value: completed.length, color: "text-success-600", bg: "bg-success-50" },
          { label: "Pending",       value: pending.length,   color: "text-danger-600",  bg: "bg-red-50" },
          { label: "Total Revenue", value: formatKSH(totalRevenue), color: "text-primary-600", bg: "bg-primary-50", small: true },
        ].map((s, i) => (
          <div key={i} className="card">
            <div className={`card-body flex flex-col items-center justify-center text-center p-4 ${s.bg} rounded-lg`}>
              <span className={`text-2xl font-bold ${s.color} ${s.small ? "text-lg break-all" : ""}`}>
                {s.value}
              </span>
              <span className="text-xs text-gray-600 mt-1">{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchOrders} className="underline text-sm ml-4">Retry</button>
        </div>
      )}

      {/* DataTable Card */}
      <div className="card">
        <div className="card-body">
          <DataTable
            key={`${statusFilter}-${dateRange}`}
            data={filtered}
            columns={columns}
            searchFields={searchFields}
            filename="orders"
            title="Orders Report"
            loading={loading}
            rowKey={row => row.id}
          >
            {/* Extra filters */}
            <div className="pb-3 border-b border-gray-100 mb-2 space-y-3">
              {/* Status tabs */}
              <div className="flex gap-1 flex-wrap">
                {[
                  { id: "all",         label: `All (${orders.length})` },
                  { id: "ordered",     label: `Completed (${completed.length})` },
                  { id: "not_ordered", label: `Pending (${pending.length})` },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setStatusFilter(tab.id)}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-colors border ${
                      statusFilter === tab.id
                        ? "bg-primary-600 border-primary-600 text-white"
                        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Date Range + Reset */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 min-w-[200px]">
                  <label className="form-label text-xs mb-0 whitespace-nowrap">Date Range:</label>
                  <div className="flex-1">
                    <SearchableSelect
                      value={dateRange}
                      onChange={setDateRange}
                      options={DATE_RANGE_OPTIONS}
                      placeholder="All Time"
                    />
                  </div>
                </div>
                {(dateRange || statusFilter !== "all") && (
                  <button
                    onClick={() => { setStatusFilter("all"); setDateRange(""); }}
                    className="btn btn-secondary text-xs py-1 inline-flex items-center gap-1"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Reset
                  </button>
                )}
                {!loading && filtered.length > 0 && (
                  <span className="text-sm text-gray-600 ml-auto">
                    Filtered Revenue:{" "}
                    <span className="font-semibold text-success-700">
                      {formatKSH(
                        filtered
                          .filter(o => o.order_status === "Ordered")
                          .reduce((s, o) => s + parseFloat(o.order_amount || 0), 0)
                      )}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </DataTable>
        </div>
      </div>

      {showOrderModal && (
        <OrderModal
          onClose={() => {
            setShowOrderModal(false);
            fetchOrders();
          }}
        />
      )}
    </div>
  );
};

export default Orders;
